import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { filter, Subject, takeUntil } from 'rxjs';
import { INotification, NgsildConnectorService } from 'ngsild-connector';
import {
  IAppDashboard,
  INgsiSubscriptionWidget,
  IPermissionsFlags,
  IPluginPermissions,
  IUser,
  ModelService,
	PluginInjectorWidget,
	PluginInjectorService
} from 'core-client/api';
import {
  IOiclOpenBridgeThrusterWidget
} from '../../shared/interfaces/thruster/oicl-openbridge-thruster-widget.interface';
import {
  IThrusterWidgetPermissions
} from '../../shared/interfaces/widget-permissions/widgetPermission.interface';
import {
  IObcNavigationInstruments
} from '../../shared/interfaces/oicl-obenbridge-interfaces/obc-navigations-instrument.interface';
import { ObcCommandStatus } from '../../shared/interfaces/oicl-obenbridge-interfaces/obc-default.interface';
import { INgsiDriveSystemType } from '../../shared/interfaces/ngsi/drive-system.interface';
import { INgsiEntity, INgsiEntityIdAttrs } from '../../shared/interfaces/ngsi/entity.interface';
import * as ModuleEntityTypesConfig from '../../shared/config/module.ngsi-entity-types.constants';
import * as AzimuthThrusterConstant from '../../shared/config/azimuth-thruster.constant';
import { ThrusterService } from '../services/thruster.service';

import '@oicl/openbridge-webcomponents/dist/navigation-instruments/azimuth-thruster-labeled/azimuth-thruster-labeled.js';


@Component({
    selector: 'lib-thruster-view',
    imports: [],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './thruster-view.component.html',
    styleUrl: './thruster-view.component.scss'
})
export class ThrusterViewComponent implements AfterViewInit, OnDestroy {
  // Services
  readonly ngsildConnectorService: NgsildConnectorService = inject(NgsildConnectorService);
  readonly pluginInjectorService: PluginInjectorService = inject(PluginInjectorService);
  readonly modelService: ModelService = inject(ModelService);
  protected readonly thrusterService: ThrusterService = inject(ThrusterService);

  // Variables
  protected obcNavigationsInstrumentsInstance: Required<Pick<IObcNavigationInstruments, 'azimuthThrusterLabeled'>> = {
    azimuthThrusterLabeled: {
      angle: 0,
      angleSetpoint: undefined,
      thrust: 0,
      thrustSetpoint: undefined,
      label: 'Thruster',
      angleAdvices: [{
        minAngle: 20,
        maxAngle: 50,
        type: 'advice',
        hinted: true
      }, {
        minAngle: 60,
        maxAngle: 100,
        type: 'caution',
        hinted: true
      }],
      thrustAdvices:  [{
        min: 20,
        max: 50,
        type: 'advice',
        hinted: true
      }, {
        min: 75,
        max: 100,
        type: 'caution',
        hinted: true
      }, {
        min: -100,
        max: -75,
        type: 'caution',
        hinted: true
      }],
      commandStatus: ObcCommandStatus.InCommand,
      topPropeller: 'single',
      bottomPropeller: 'cap'
    }
  };

  @ViewChild('obcNavigationInstrumentContainer',{ static: false })
  protected obcNavigationInstrumentContainer!: ElementRef;

  @ViewChild('azimuthThrusterLabeledContainer', { static: true })
  protected azimuthThrusterLabeledContainer!: ElementRef;

  @Input()
  data: PluginInjectorWidget.IWidget | undefined;

  @Input()
  isHeaderHovered: boolean = false;

  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  protected openBridgeThrusterWidget: IOiclOpenBridgeThrusterWidget | undefined;
  protected widgetId: string | undefined;
  protected currDashboardId: string | undefined;
  protected ngsiEntityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> | undefined;
  protected entityType: string = ModuleEntityTypesConfig.NGSI_ENTITY_TYPES.thruster.entityTypes[0].DriveSystem;
  private thrusterId: number | undefined;
  protected widgetPermissions: IThrusterWidgetPermissions = {
    widgetEnabled: false,
    widgetSettingsEditEnabled: false,
    ngsildConnectorEnabled: false,
  };
  protected extraPermissionRefs: { ngsildConnectorEnabled: string } = {
    ngsildConnectorEnabled: 'authorizeNgsildConnector'
  };
  private widgetSubscriptions: INgsiSubscriptionWidget[] = [];
  private resizeObserver: ResizeObserver | undefined;


  async ngAfterViewInit(): Promise<void> {
    if (!this.data?.appPluginWidget) {
      console.error(`Widget data not correctly loaded.`);
      return;
    }

    // Workaround to fix issue with fullscreen modus
    this.applyAzimuthThrusterMaxHeight();
    // Observe changes such as onHover for widget headers
    this.observeStyleChanges();

    this.widgetSubscriptions = await this.modelService._ngsiSubscriptionWidget.listAll(`type=${this.entityType}`);

    await this.getWidgetAccessRights(
        this.modelService._user.getUser(),
        this.pluginInjectorService,
        this.data
    );

    const user = this.modelService._user.getUser();
    if (!user?._id) {
      console.error(`[Compass Widget] User not found.`);
      return;
    }

    this.currDashboardId = user.active_dashboard;

    this.openBridgeThrusterWidget = this.data.appPluginWidget;
    this.widgetId = this.openBridgeThrusterWidget?._id;
    this.ngsiEntityId = this.openBridgeThrusterWidget?.settings.entityId;
    this.thrusterId = this.openBridgeThrusterWidget?.settings.thrusterId;

    // observe ngsild-proxy socketIo connection
    if (this.widgetPermissions.ngsildConnectorEnabled) {
      // Query NGSI infos of entity
      if (this.ngsiEntityId?.idPattern) {
        await this.fetchLatestThrusterInfos(this.ngsiEntityId);
      }

      this.listenOnNgsildServiceEvents();
    }

    // Observe setting changes
    this.listenOnWidgetSettingsChanges(this.widgetId);
  }

  private applyAzimuthThrusterMaxHeight(): void {
    if (!this.azimuthThrusterLabeledContainer) {
      return;
    }

    const el = this.azimuthThrusterLabeledContainer.nativeElement as HTMLElement;

    // Check whether Shadow Root exists
    const shadowRoot = el.shadowRoot;
    if (!shadowRoot) {
      console.warn('Shadow root not available for <obc-azimuth-thruster>.');
      return;
    }

    // Check whether style has already been injected (avoid duplicate entries)
    if (shadowRoot.querySelector('style[data-style="azimuth-thruster-max-height"]')) {
      return;
    }

    const style = document.createElement('style');
    style.setAttribute('data-style', 'azimuth-thruster-max-height');
    style.textContent = `
    .azimuth-thruster {
      max-height: 90vh !important;
    }
  `;

    shadowRoot.appendChild(style);
  }

  private async getWidgetAccessRights(
      user: IUser,
      pluginInjectorService: PluginInjectorService,
      widget: PluginInjectorWidget.IWidget
  ): Promise<void> {
    const appDashboard = user.app_dashboards.find(
        (dashboard: IAppDashboard) => dashboard._id === user.active_dashboard
    );

    if (appDashboard) {
      const pluginPermissions = await pluginInjectorService.permissionService.getPermissions(
          appDashboard,
          user
      );

      const accessRights = pluginPermissions.find(
          (plugin_permission: IPluginPermissions) => plugin_permission.plugin_id === widget?.pluginId
      );

      if (accessRights?.widget_permissions) {
        const modulePermissions = accessRights.widget_permissions[widget.pluginModule.toLowerCase()] as IPermissionsFlags;

        const widgetEnabled = widget?.widgetPermissions?.widgetEnabledPermission?.permissionRef;
        const widgetSettingsEnabled =
            widget?.widgetPermissions?.widgetBasePermissions?.widgetSettingsEnabledPermission?.permissionRef;
        const ngsildConnectorEnabled =
            widget?.widgetPermissions?.widgetAdditionalPermissions?.[this.extraPermissionRefs.ngsildConnectorEnabled]?.permissionRef;

        this.widgetPermissions = {
          widgetEnabled: widgetEnabled ? modulePermissions[widgetEnabled] : false,
          widgetSettingsEditEnabled: widgetSettingsEnabled ? modulePermissions[widgetSettingsEnabled] : false,
          ngsildConnectorEnabled: ngsildConnectorEnabled ? modulePermissions[ngsildConnectorEnabled] : false
        };
      }
    }
  }

  private async fetchLatestThrusterInfos(entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): Promise<void> {
    const widgetEntityId = this.getWidgetEntityId(entityId);

    if (widgetEntityId === undefined) {
      return;
    }

    const uuid = await this.ngsildConnectorService.uuidFromSettings();
    const args = {
      uuid: uuid,
      idPattern: widgetEntityId
    };

    this.ngsildConnectorService.queryEntitiesById$(args)
    .pipe(takeUntil(this.ngUnsubscribe$))
    .subscribe((result: INgsiEntity[])=> {
      if (result.length === 0) {
        console.log(`NGSI query has not returned any data.`)
        return;
      }

      const entity = result[0] as INgsiDriveSystemType;
      this.updateThrusterInfos(entity);
    })
  }

  private listenOnNgsildServiceEvents(): void {
    // Register when the component is called up
    this.registerForNotification(this.widgetId, this.ngsiEntityId);

    // Listen if the plugin is switch from deactivate to activate and widget is on dashboard visible
    this.ngsildConnectorService.socket.on('connect',() => {
      if (this.ngsildConnectorService.socket.ioSocket.connected && this.widgetPermissions.ngsildConnectorEnabled) {
        this.registerForNotification(this.widgetId, this.ngsiEntityId);
      }
    });
  }

  private registerForNotification(widgetId: string | undefined, ngsiEntityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): void {
    if (!widgetId) {
      console.info('Thruster WidgetId is not assigned  - registration is aborted');
      return;
    }

    if (!ngsiEntityId || !this.currDashboardId) {
      return;
    }

    this.ngsildConnectorService.registerNotificationHandler(widgetId, this.currDashboardId,<string>this.openBridgeThrusterWidget?.class,(notification: INotification) => {
      const isRelevant = this.filterIncomingNgsINotification(
          notification,
          widgetId,
          ngsiEntityId,
          this.thrusterId,
          this.widgetSubscriptions
      )

      if (isRelevant) {
        const entity = notification.data[0] as INgsiDriveSystemType;
        this.updateThrusterInfos(entity);
      }
    });
  }

  private listenOnWidgetSettingsChanges(widgetId: string): void {
    this.thrusterService.getThrusterId$(widgetId)
    .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((thrusterId: number | undefined): thrusterId is number =>
            thrusterId != undefined &&
            thrusterId != this.thrusterId
        ),
    )
    .subscribe(async (thrusterId: number) => {
      if (!this.widgetId) {
        console.error(`Widget data not correctly loaded.`);
        return;
      }

      this.thrusterId = thrusterId;

      if (this.ngsiEntityId) {
        this.widgetSubscriptions = await this.modelService._ngsiSubscriptionWidget.listAll(`type=${this.entityType}`);

        this.listenOnNgsildServiceEvents();
      }
    })

    this.thrusterService.getThrusterEntity$(widgetId)
    .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((entityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> | undefined): entityId is Omit<INgsiEntityIdAttrs, 'ngsildPrefix'> =>
            entityId != undefined &&
            entityId.idPattern != this.ngsiEntityId?.idPattern
        ),
    )
    .subscribe(async (entityId: Omit<INgsiEntityIdAttrs, 'ngsildPrefix'>) => {
      if (!this.widgetId) {
        console.error(`Widget data not correctly loaded.`);
        return;
      }

      this.ngsiEntityId = entityId;

      if (this.thrusterId) {
        this.widgetSubscriptions = await this.modelService._ngsiSubscriptionWidget.listAll(`type=${this.entityType}`);

        this.listenOnNgsildServiceEvents();
      }
    })
  }

  private filterIncomingNgsINotification(
      notification: INotification,
      widgetId: string | undefined,
      entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined,
      thrusterId: number | undefined,
      widgetSubscriptions: INgsiSubscriptionWidget[]
  ): boolean {
    const entity = notification.data[0] as INgsiDriveSystemType;
    const widgetEntityId = this.getWidgetEntityId(entityId);

    const subForWidgetExist = widgetSubscriptions.some((widgetSubscription: INgsiSubscriptionWidget) =>
        widgetSubscription.subscriptionId === notification.subscriptionId &&
        widgetSubscription.app_plugin_widget === widgetId
    );
    const sameEntityId = entity.id === widgetEntityId;
    const sameThrusterId = entity.thruster === thrusterId;

    return (subForWidgetExist && sameEntityId && sameThrusterId);
  }

  private getWidgetEntityId(entityId: Omit<INgsiEntityIdAttrs, "ngsildPrefix"> | undefined): string | undefined {
    if (!entityId?.idPattern) {
      return undefined;
    }

    return entityId?.owner ?
        `${ModuleEntityTypesConfig.NGSI_ENTITY_ID_PREFIX}${entityId?.type}:${entityId?.owner}:${entityId?.idPattern}` :
        `${ModuleEntityTypesConfig.NGSI_ENTITY_ID_PREFIX}${entityId?.type}:${entityId?.idPattern}`;
  }

  private updateThrusterInfos(entity: INgsiDriveSystemType): void {
    if (entity.thruster !== this.thrusterId) {
      // console.log('Not the selected thrusterId.')
      return;
    }

    // Source: SG:TRD
    if (entity?.source === AzimuthThrusterConstant.THRUSTER_RESPONSE_SOURCE_SELECTOR) {
      if (entity?.rpmResponse) {
        this.obcNavigationsInstrumentsInstance.azimuthThrusterLabeled.thrust = Math.round(entity.rpmResponse);
      }

      if (entity?.azimutResponse) {
        this.obcNavigationsInstrumentsInstance.azimuthThrusterLabeled.angle = Math.round(entity.azimutResponse);
      }
    }

    // Source: SG:TRC
    if (entity?.source === AzimuthThrusterConstant.THRUSTER_DEMAND_SOURCE_SELECTOR) {
      if (entity?.rpmDemand) {
        this.obcNavigationsInstrumentsInstance.azimuthThrusterLabeled.thrustSetpoint = Math.round(entity.rpmDemand);
      }

      if (entity?.azimutDemand) {
        this.obcNavigationsInstrumentsInstance.azimuthThrusterLabeled.angleSetpoint =entity.azimutDemand;
      }
    }
  }

  private observeStyleChanges(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.setWidgetHeaderOnHover(this.isHeaderHovered);
    });

    if (this.obcNavigationInstrumentContainer?.nativeElement) {
      this.resizeObserver.observe(this.obcNavigationInstrumentContainer?.nativeElement)
    }
  }

  private setWidgetHeaderOnHover(isHeaderHovered: boolean): void {
    if (this.obcNavigationInstrumentContainer?.nativeElement) {
      this.obcNavigationInstrumentContainer.nativeElement.style.marginTop = isHeaderHovered ? '35px' : '0';
    }
  }

  ngOnDestroy(): void {
    // Clear to all component subscriptions
    this.ngUnsubscribe$.complete();

    // Clear extern plugins subscriptions
    if (this.widgetId) {
      this.ngsildConnectorService.unregisterNotificationHandler(this.widgetId, <string>this.openBridgeThrusterWidget?.class);
      this.thrusterService.removeThrusterEntity(this.widgetId);
      this.thrusterService.removeThrusterId(this.widgetId);
    }

    // Clean up ResizeObserver
    this.resizeObserver?.disconnect();
  }
}
